import { Select, Spin } from 'antd'
import { SelectProps } from 'antd/es/select'
import debounce from 'lodash-es/debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
const { Option } = Select
export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<ValueType[]>
  debounceTimeout?: number
  triggerKey?: any
}

function DebounceSelect<
  ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any,
>({ fetchOptions, debounceTimeout = 800, triggerKey, ...props }: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState<ValueType[]>([])
  const fetchRef = useRef(0)

  useEffect(() => {
    debounceFetcher(triggerKey)
  }, [triggerKey])

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      setOptions([])
      if (!value) return
      fetchRef.current += 1
      const fetchId = fetchRef.current
      setFetching(true)

      fetchOptions(value).then(newOptions => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return
        }

        setOptions(newOptions)
        setFetching(false)
      })
    }

    return debounce(loadOptions, debounceTimeout)
  }, [fetchOptions, debounceTimeout])

  return (
    <Select
      showSearch
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
    >
      {options &&
        options.map((item: any) => (
          <Option key={item?.id} value={item?.value}>
            {item?.label}
          </Option>
        ))}
    </Select>
  )
}
export default DebounceSelect
